/**
 * Studio API – video management (local / server, not AT Protocol).
 * Base URL should match the upload server (e.g. https://shxre.social).
 */

/** Optional Basic Auth for Studio and presigned URL (set EXPO_PUBLIC_STUDIO_BASIC_AUTH to "user:password" if server uses basic-auth-password). */
export function getStudioAuthHeaders(): Record<string, string> {
  const raw =
    typeof process !== 'undefined' &&
    process.env?.EXPO_PUBLIC_STUDIO_BASIC_AUTH?.trim()
  if (!raw) return {}
  if (raw.startsWith('Basic ')) return {Authorization: raw}
  try {
    return {Authorization: `Basic ${btoa(raw)}`}
  } catch {
    return {}
  }
}

export const STUDIO_API_BASE =
  (typeof process !== 'undefined' &&
    process.env?.EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT?.replace(
      '/api/s3/presigned-url',
      '',
    )) ||
  'https://shxre.social'

export type StudioVideoStatus =
  | 'draft'
  | 'uploading'
  | 'processing'
  | 'published'

/** Short = vertical/Clips (9:16); standard = horizontal/SHXRE Videos (16:9). */
export type StudioVideoFormat = 'short' | 'standard'

export interface StudioVideo {
  id: string
  user_id: string
  title: string
  description: string | null
  video_path: string
  thumbnail_path: string | null
  status: StudioVideoStatus
  total_views: number
  created_at: string
  updated_at: string
  /** Bluesky DID of owner; only show on My Videos when this matches current user */
  owner_did?: string
  /** short = vertical/Clips feed; standard = horizontal SHXRE Videos feed. Defaults to standard. */
  video_format?: StudioVideoFormat
  /** When the backend supports likes. */
  like_count?: number
  liked_by_me?: boolean
}

export interface StudioVideoAnalyticsDay {
  view_date: string
  views_count: number
}

export interface StudioVideoCreateInput {
  title: string
  description?: string
  video_path: string
  thumbnail_path?: string
  status?: StudioVideoStatus
  /** Required so the video appears in My Videos; never omit. */
  owner_did: string
  /** short = Clips (vertical); standard = SHXRE Videos (horizontal). Defaults to standard. */
  video_format?: StudioVideoFormat
}

export interface StudioVideoUpdateInput {
  title?: string
  description?: string
  thumbnail_path?: string
  status?: StudioVideoStatus
  video_format?: StudioVideoFormat
}

async function studioFetch<T>(
  path: string,
  opts?: RequestInit & {params?: Record<string, string>},
): Promise<T> {
  const url =
    path.startsWith('http') || path.startsWith('//')
      ? path
      : `${STUDIO_API_BASE}${path.startsWith('/') ? '' : '/'}${path}`
  const query =
    opts?.params && Object.keys(opts.params).length
      ? `?${new URLSearchParams(opts.params).toString()}`
      : ''
  const {params: _p, ...rest} = opts ?? {}
  const res = await fetch(`${url}${query}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
      ...rest.headers,
    },
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Studio API ${res.status}`)
  }
  return res.json() as Promise<T>
}

export async function studioListVideos(opts?: {
  status?: StudioVideoStatus
  owner_did?: string
  /** When set, only return videos of this format (e.g. 'short' for Clips). */
  video_format?: StudioVideoFormat
}): Promise<StudioVideo[]> {
  try {
    const params: Record<string, string> = {}
    if (opts?.status != null) params.status = opts.status
    if (opts?.owner_did) params.owner_did = opts.owner_did
    if (opts?.video_format) params.video_format = opts.video_format
    const data = await studioFetch<{videos?: StudioVideo[]}>(
      '/api/studio/videos',
      Object.keys(params).length ? {params} : {},
    )
    const list = data.videos ?? []
    if (opts?.video_format && list.length > 0) {
      return list.filter(
        v => (v.video_format ?? 'standard') === opts.video_format,
      )
    }
    return list
  } catch {
    return []
  }
}

export async function studioGetVideo(id: string): Promise<StudioVideo | null> {
  try {
    return await studioFetch<StudioVideo>(`/api/studio/videos/${id}`)
  } catch {
    return null
  }
}

/** Record a view for a video (e.g. after 20s playback). Idempotent; call once per watch. */
export async function studioRecordView(videoId: string): Promise<void> {
  const path = `/api/studio/videos/${videoId}/view`
  const url = `${STUDIO_API_BASE}${path}`
  const res = await fetch(url, {
    method: 'POST',
    headers: {...getStudioAuthHeaders()},
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Studio API ${res.status}`)
  }
  if (res.status !== 204) {
    await res.json()
  }
}

export async function studioCreateVideo(
  input: StudioVideoCreateInput,
): Promise<StudioVideo> {
  if (!input.owner_did || typeof input.owner_did !== 'string') {
    throw new Error('owner_did is required to create a video')
  }
  const body = {
    title: input.title,
    description: input.description,
    video_path: input.video_path,
    thumbnail_path: input.thumbnail_path,
    status: input.status,
    owner_did: input.owner_did,
    video_format: input.video_format,
  }
  return studioFetch<StudioVideo>('/api/studio/videos', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function studioUpdateVideo(
  id: string,
  input: StudioVideoUpdateInput,
): Promise<StudioVideo> {
  return studioFetch<StudioVideo>(`/api/studio/videos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export async function studioGetAnalytics(
  videoId?: string,
): Promise<StudioVideoAnalyticsDay[]> {
  try {
    const params = videoId ? {video_id: videoId} : undefined
    const data = await studioFetch<{analytics?: StudioVideoAnalyticsDay[]}>(
      '/api/studio/analytics',
      params ? {params} : {},
    )
    return data.analytics ?? []
  } catch {
    return []
  }
}

export interface StudioVideoComment {
  id: string
  video_id: string
  author_id: string
  author_handle: string | null
  text: string
  created_at: string
}

export async function studioGetVideoComments(
  videoId: string,
): Promise<StudioVideoComment[]> {
  try {
    const data = await studioFetch<{comments?: StudioVideoComment[]}>(
      `/api/studio/videos/${videoId}/comments`,
    )
    return data.comments ?? []
  } catch {
    return []
  }
}

export async function studioCreateVideoComment(
  videoId: string,
  input: {text: string; author_id?: string; author_handle?: string},
): Promise<StudioVideoComment> {
  return studioFetch<StudioVideoComment>(
    `/api/studio/videos/${videoId}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  )
}

/** Like a Studio video. Backend may not implement yet; 404 is handled by the caller. */
export async function studioLikeVideo(videoId: string): Promise<{like_count: number}> {
  return studioFetch<{like_count: number}>(
    `/api/studio/videos/${videoId}/like`,
    {method: 'POST'},
  )
}

/** Remove like from a Studio video. */
export async function studioUnlikeVideo(videoId: string): Promise<{like_count: number}> {
  return studioFetch<{like_count: number}>(
    `/api/studio/videos/${videoId}/like`,
    {method: 'DELETE'},
  )
}

// --- Articles (long-form) ---

export type StudioArticleStatus = 'draft' | 'published'

export interface StudioArticle {
  id: string
  user_id: string
  owner_did?: string
  title: string
  content: string
  cover_image_path: string | null
  status: StudioArticleStatus
  created_at: string
  updated_at: string
  published_at: string | null
}

export interface StudioArticleCreateInput {
  title: string
  content?: string
  cover_image_path?: string
  status?: StudioArticleStatus
  /** Required so the article appears in your list. */
  owner_did: string
}

export interface StudioArticleUpdateInput {
  title?: string
  content?: string
  cover_image_path?: string
  status?: StudioArticleStatus
}

export async function studioListArticles(opts?: {
  owner_did?: string
  status?: StudioArticleStatus
}): Promise<StudioArticle[]> {
  try {
    const params: Record<string, string> = {}
    if (opts?.owner_did) params.owner_did = opts.owner_did
    if (opts?.status) params.status = opts.status
    const data = await studioFetch<{articles?: StudioArticle[]}>(
      '/api/studio/articles',
      Object.keys(params).length ? {params} : {},
    )
    return data.articles ?? []
  } catch {
    return []
  }
}

export async function studioGetArticle(id: string): Promise<StudioArticle | null> {
  try {
    return await studioFetch<StudioArticle>(`/api/studio/articles/${id}`)
  } catch {
    return null
  }
}

export async function studioCreateArticle(
  input: StudioArticleCreateInput,
): Promise<StudioArticle> {
  if (!input.owner_did || typeof input.owner_did !== 'string') {
    throw new Error('owner_did is required to create an article')
  }
  const body = {
    title: input.title,
    content: input.content ?? '',
    cover_image_path: input.cover_image_path,
    status: input.status,
    owner_did: input.owner_did,
  }
  return studioFetch<StudioArticle>('/api/studio/articles', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function studioUpdateArticle(
  id: string,
  input: StudioArticleUpdateInput,
): Promise<StudioArticle> {
  return studioFetch<StudioArticle>(`/api/studio/articles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export async function studioDeleteArticle(id: string): Promise<void> {
  const url = `${STUDIO_API_BASE}/api/studio/articles/${id}`
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {...getStudioAuthHeaders()},
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Studio API ${res.status}`)
  }
}
